import { useNavigate } from 'react-router-dom';
import { AlertCircle, Sparkles, Zap, Crown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const SubscriptionLimitModal = ({ isOpen, onClose, subscription }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const plan = subscription?.plan || 'free';
  const used = subscription?.analyses_used || 0;
  const limit = subscription?.analyses_limit || 5;

  const plans = [
    { name: 'Student', price: '$9', analyses: '50', color: 'from-green-500 to-emerald-600' },
    { name: 'Research', price: '$29', analyses: '200', color: 'from-blue-500 to-cyan-600' },
    { name: 'Lab', price: '$99', analyses: 'Unlimited', color: 'from-purple-500 to-indigo-600' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 rounded-full hover:bg-slate-100 transition-colors z-10"
          data-testid="close-limit-modal"
        >
          <X className="w-5 h-5 text-slate-400" />
        </button>

        {/* Header */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 px-6 py-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/20 mb-4">
            <AlertCircle className="w-8 h-8 text-amber-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Analysis Limit Reached</h2>
          <p className="text-slate-300">
            You've used all {limit} free analyses this month
          </p>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {/* Current usage */}
          <div className="mb-6 p-4 bg-slate-50 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-600">Current Plan</span>
              <span className="text-sm font-semibold text-slate-900 capitalize">{plan}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Analyses Used</span>
              <span className="text-sm font-semibold text-slate-900">{used} / {limit}</span>
            </div>
            <div className="mt-3 h-2 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-red-500 to-orange-500 rounded-full"
                style={{ width: '100%' }}
              />
            </div>
          </div>

          {/* Upgrade options */}
          <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-500" />
            Upgrade for more analyses
          </h3>

          <div className="space-y-2 mb-6">
            {plans.map((p) => (
              <div 
                key={p.name}
                className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-colors cursor-pointer"
                onClick={() => {
                  onClose();
                  navigate('/subscription');
                }}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${p.color} flex items-center justify-center`}>
                    {p.name === 'Lab' ? (
                      <Crown className="w-4 h-4 text-white" />
                    ) : (
                      <Zap className="w-4 h-4 text-white" />
                    )}
                  </div>
                  <div>
                    <div className="font-medium text-slate-900">{p.name}</div>
                    <div className="text-xs text-slate-500">{p.analyses} analyses/month</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-slate-900">{p.price}</div>
                  <div className="text-xs text-slate-500">/month</div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <Button
            onClick={() => {
              onClose();
              navigate('/subscription');
            }}
            className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white py-3"
            data-testid="upgrade-now-button"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Upgrade Now
          </Button>

          <p className="text-center text-xs text-slate-500 mt-4">
            Cancel anytime. Secure payment via Stripe.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionLimitModal;
